"use client";

// Server-rendered content that the browser hydrates later: once it nears the viewport, or on the
// first interaction inside it. Until then React leaves its server HTML in place, untouched (a
// Suspense boundary that suspends while hydrating keeps its server markup), so the page looks the
// same and costs no main-thread time for content nobody has scrolled to or used yet.
//
// React 19 has no partial hydration API; this is its Suspense mechanism. A boundary that is still
// waiting when a context above it changes can't hydrate with the old value, so React renders it
// afresh with the new one instead, in the same commit (no fallback is shown: the content only waits
// while hydrating). Keep values that change after hydration out of context where cards would read
// them (the docs language is an external store for that reason, see DocsLanguageProvider).
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  Suspense,
  startTransition,
  use,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  type DeferredHydrationTrigger,
  type Gate,
  getGate,
  isDeferredHydrationArmed,
} from "./DeferredHydrationGates";

const subscribeToNothing = () => () => {};
/** True while React hydrates this component (it reads the server snapshot only then). */
const useIsHydrating = () =>
  useSyncExternalStore(
    subscribeToNothing,
    () => false,
    () => true,
  );

const DeferredContent = (props: { gate: Gate; released: boolean; children: ReactNode }) => {
  "use no memo";
  const { gate, children } = props;
  const hydrating = useIsHydrating();
  // Suspending while hydrating keeps the server HTML; React retries once the promise resolves.
  if (isDeferredHydrationArmed() && hydrating && !gate.released) use(gate.promise as Promise<void>);
  return children;
};

/** Whether the URL's fragment points into `element` (or to a collapsed item one of its trees has). */
const isHashTarget = (element: HTMLElement) => {
  const id = decodeURIComponent(window.location.hash.slice(1));
  if (!id) return false;
  const target = document.getElementById(id);
  if (target) return element.contains(target);
  // Trees render collapsed items only when opened, so their anchors aren't in the document yet:
  // `data-anchor-scope` names the prefix of the ids a tree's items get.
  return [...element.querySelectorAll<HTMLElement>("[data-anchor-scope]")].some((scope) =>
    id.startsWith(`${scope.dataset.anchorScope}.`),
  );
};

/** Pixels around the viewport within which "visible" boundaries hydrate ahead of being seen. */
const VISIBLE_MARGIN = "100% 0px";

type WrapperProps = Omit<ComponentPropsWithoutRef<"div">, "children">;

export interface DeferredHydrationProps extends WrapperProps {
  /**
   * The boundaries that hydrate together. Boundaries sharing state (a provider whose value changes
   * when one of them hydrates) belong to one group, so the others hydrate rather than re-render.
   */
  group: string;
  trigger: DeferredHydrationTrigger;
  /** The wrapping element, which hydrates right away and watches for the trigger. */
  as?: "div" | "section";
  children: ReactNode;
}

/**
 * Content that hydrates when it nears the viewport (`trigger="visible"`) or on the first pointer,
 * touch or focus inside it (`"interaction"`); also when the URL's fragment points into it. The
 * server renders it in full, and client-side renders (navigations) render it right away. Clicks and
 * key presses on content that hasn't hydrated yet hydrate it first (React's selective hydration).
 */
const DeferredHydration = (props: DeferredHydrationProps) => {
  const { group, trigger, as: Element = "div", children, ...wrapperProps } = props;
  const gate = getGate(group, trigger);
  const ref = useRef<HTMLDivElement>(null);
  // Resolving the promise alone isn't enough: React may not have tried this boundary yet (it
  // hydrates boundaries it meets later at an idle priority, and that work waits behind other
  // boundaries that are still suspended). New props make React hydrate it now, in a transition.
  const [released, setReleased] = useState(false);

  useEffect(() => {
    // An interaction hydrates its boundary right away; boundaries coming into view hydrate in a
    // transition, which React works on in slices between other tasks.
    const onRelease = () =>
      trigger === "interaction" ? setReleased(true) : startTransition(() => setReleased(true));
    gate.listeners.add(onRelease);
    if (gate.released) onRelease();
    return () => {
      gate.listeners.delete(onRelease);
    };
  }, [gate, trigger]);

  useEffect(() => {
    const element = ref.current;
    if (!element || gate.released) return;
    const release = () => gate.release();
    if (isHashTarget(element)) {
      release();
      return;
    }
    const onHashChange = () => {
      if (isHashTarget(element)) release();
    };
    window.addEventListener("hashchange", onHashChange);
    let observer: IntersectionObserver | undefined;
    if (trigger === "visible") {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) release();
        },
        { rootMargin: VISIBLE_MARGIN },
      );
      observer.observe(element);
    }
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      observer?.disconnect();
    };
  }, [gate, trigger]);

  return (
    <Element
      ref={ref}
      {...wrapperProps}
      data-deferred-hydration={group}
      data-deferred-hydration-trigger={trigger}
    >
      <Suspense fallback={null}>
        <DeferredContent gate={gate} released={released}>
          {children}
        </DeferredContent>
      </Suspense>
    </Element>
  );
};

export default DeferredHydration;
